import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createVortxTheme } from '../core/index.js';

/**
 * Context for theme mode state
 * @private
 */
export const ThemeModeContext = createContext();

/**
 * ThemeModeProvider - Manages light/retro theme mode with localStorage persistence
 *
 * Provides theme mode switching between light and retro modes, with automatic
 * localStorage persistence and migration from legacy 'dark' mode.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 *
 * @example
 * // Wrap your app with ThemeModeProvider
 * import { ThemeModeProvider } from '@vortx/shared-ui/theme-mode';
 *
 * function App() {
 *   return (
 *     <ThemeModeProvider>
 *       <YourApp />
 *     </ThemeModeProvider>
 *   );
 * }
 *
 * @example
 * // Use the theme mode in components
 * import { useThemeMode } from '@vortx/shared-ui/theme-mode';
 *
 * function MyComponent() {
 *   const { mode, isRetro, toggleMode } = useThemeMode();
 *
 *   return (
 *     <button onClick={toggleMode}>
 *       Current: {mode} mode
 *     </button>
 *   );
 * }
 */
export function ThemeModeProvider({ children }) {
    // Initialize mode from localStorage with migration support
    const [mode, setMode] = useState(() => {
        const saved = localStorage.getItem('vortx-theme-mode');

        // Migrate 'dark' to 'retro' for existing users
        if (saved === 'dark') {
            localStorage.setItem('vortx-theme-mode', 'retro');
            // Clean up old retro flag if it exists
            localStorage.removeItem('vortx-retro-mode');
            return 'retro';
        }

        // Default to retro for new users
        return saved || 'retro';
    });

    // Persist mode changes to localStorage
    useEffect(() => {
        localStorage.setItem('vortx-theme-mode', mode);
    }, [mode]);

    /**
     * Toggle between light and retro modes
     */
    const toggleMode = useCallback(() => {
        setMode(prev => prev === 'light' ? 'retro' : 'light');
    }, []);

    // Create theme based on current mode
    const theme = useMemo(() => {
        return createVortxTheme(mode);
    }, [mode]);

    // Computed flags for convenience
    const isRetro = mode === 'retro';
    const isLight = mode === 'light';
    const isDark = isRetro; // Backwards compatibility alias

    const value = {
        mode,
        isDark,
        isLight,
        isRetro,
        toggleMode,
        setMode,
    };

    return (
        <ThemeModeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
}
